/* iter174 + iter177 — Public Promo landing page.
   Loaded by both /offer (which renders <PromoPage slug="offer" />) and
   /promo/:slug. Fetches /api/public/promo/{slug}; if destination_type
   === 'redirect' and the slug is active, performs a window.location.href
   redirect to the configured URL. Otherwise renders a branded landing
   card with Call + Request Service buttons AND a public intake form
   below that lets customers self-book straight from the QR code.
*/
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Phone, Wrench, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NEED_OPTIONS = [
  { value: 'plumbing_repair', label: 'Plumbing Repair' },
  { value: 'drain_cleaning', label: 'Drain Cleaning' },
  { value: 'water_heater', label: 'Water Heater' },
  { value: 'toilet', label: 'Toilet' },
  { value: 'faucet_sink', label: 'Faucet / Sink' },
  { value: 'sump_pump', label: 'Sump Pump' },
  { value: 'sewer_main_line', label: 'Sewer / Main Line' },
  { value: 'water_line', label: 'Water Line' },
  { value: 'gas_line', label: 'Gas Line' },
  { value: 'remodel_rough_in', label: 'Remodel / Rough-In' },
  { value: 'membership_maintenance', label: 'Membership / Maintenance' },
  { value: 'commercial_plumbing', label: 'Commercial Plumbing' },
  { value: 'property_management', label: 'Property Management' },
  { value: 'other', label: 'Other' },
];

const URGENCY_OPTIONS = [
  { value: 'emergency_today', label: 'Emergency / Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'flexible', label: 'Flexible' },
];

const CONTACT_OPTIONS = [
  { value: 'call', label: 'Call' },
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
];

const CONSENT_TEXT =
  'By submitting this form, I agree to receive calls, texts, and emails from NexFlow Plumbing & Drain Services about my request and promotional offers. Message and data rates may apply. Consent is not required as a condition of purchase. Reply STOP to opt out.';

// iter186 — Giveaway campaigns must use giveaway-specific consent wording.
// "about my request" is service-flow language and reads wrong on a giveaway
// entry form. This checkbox stays REQUIRED for giveaway entries so NexFlow
// has explicit permission to contact winners + market future giveaways.
const GIVEAWAY_CONSENT_TEXT =
  'By submitting this form, I agree to receive calls, texts, and emails from NexFlow Plumbing & Drain Services about my giveaway entry and promotional offers. Message and data rates may apply. Consent is not required as a condition of purchase. Reply STOP to opt out.';

const blankForm = () => ({
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  zip: '',
  need: '',
  urgency: '',
  preferred_contact: '',
  notes: '',
  consent: false,
  rules_consent: false,
});

// iter180 — Format raw 10-digit US phone numbers as "xxx-xxx-xxxx" for display.
const fmtPhone = (raw) => {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith('1')) {
    const r = digits.slice(1);
    return `${r.slice(0, 3)}-${r.slice(3, 6)}-${r.slice(6)}`;
  }
  return raw;
};

const PromoIntakeForm = ({ slug, callNumber, campaign, campaignTypeFallback }) => {
  // iter185 — Belt-and-suspenders: honor either the nested campaign.campaign_type
  // OR the top-level data.campaign_type fallback (used when the /offer promo_link
  // itself is a giveaway but no scheduled campaign is active today).
  // iter188 — GIVEAWAY campaigns must render a giveaway ENTRY form (name/phone/email/ZIP
  // + consent) — never the service-request fields. A post-entry secondary CTA can flip
  // the page into the normal service-request flow via serviceMode.
  const [serviceMode, setServiceMode] = useState(false);
  const isGiveaway =
    (campaign?.campaign_type === 'giveaway' || campaignTypeFallback === 'giveaway') && !serviceMode;
  const promoName = campaign?.name || 'NexFlow';
  const [form, setForm] = useState(blankForm());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    const required = isGiveaway
      ? [
          ['first_name', 'First name'],
          ['last_name', 'Last name'],
          ['phone', 'Mobile phone'],
        ]
      : [
          ['first_name', 'First name'],
          ['last_name', 'Last name'],
          ['phone', 'Mobile phone'],
          ['address', 'Service address'],
          ['city', 'City'],
          ['zip', 'ZIP'],
        ];
    for (const [k, label] of required) {
      if (!String(form[k] || '').trim()) {
        setError(`${label} is required`);
        return;
      }
    }
    if (!form.consent) {
      setError(isGiveaway
        ? 'Please check the marketing consent box to enter the giveaway.'
        : 'Please check the consent box to submit.');
      return;
    }
    if (isGiveaway && !form.rules_consent) {
      setError('Please accept the giveaway rules to enter.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        slug,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        zip: form.zip.trim(),
        needs: form.need ? [form.need] : [],
        urgency: form.urgency || null,
        preferred_contact: form.preferred_contact || null,
        notes: form.notes.trim(),
        consent: true,
        rules_consent: !!form.rules_consent,
      };
      const { data } = await axios.post(`${API}/public/promo-intake-v2`, payload);
      setDone(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please call us instead.');
    } finally {
      setSubmitting(false);
    }
  };

  const callDigits = (callNumber || '614-285-4509').replace(/[^0-9+]/g, '');
  const callDisplay = fmtPhone(callNumber || '614-285-4509');

  if (done) {
    return (
      <div
        id="promo-intake"
        data-testid="promo-intake-success"
        className="w-full max-w-md bg-[#141414] border border-[#262626] rounded-2xl p-7 shadow-2xl mt-6"
      >
        <div className="flex items-center gap-3 text-[#D4AF37]">
          <CheckCircle2 className="w-7 h-7" />
          <h2 className="font-['Outfit'] text-2xl font-bold text-white">
            {isGiveaway ? 'You’re entered!' : 'Request Received'}
          </h2>
        </div>
        <p className="text-[#C9C9C9] mt-4 leading-relaxed">
          {isGiveaway ? (
            <>Your entry has been received for the current NexFlow giveaway{promoName && promoName !== 'NexFlow' ? <> (<strong className="text-white">{promoName}</strong>)</> : ''}. We&apos;ll contact you if you win. For urgent service, call{' '}</>
          ) : (
            <>Thanks! NexFlow received your request. Our office will contact you shortly. For urgent service, call{' '}</>
          )}
          <a href={`tel:${callDigits}`} className="text-[#D4AF37] font-bold" data-testid="promo-intake-success-call">
            {callDisplay}
          </a>.
        </p>
        {isGiveaway && (
          <p className="text-[#737373] text-[11px] mt-3 italic">No purchase necessary. Some restrictions apply.</p>
        )}
        {isGiveaway && (
          <button
            type="button"
            data-testid="promo-secondary-request-service"
            onClick={() => { setDone(null); setForm(blankForm()); setServiceMode(true); setError(''); }}
            className="mt-5 w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#262626] text-white border border-[#3a3a3a] rounded-lg font-semibold hover:bg-[#3a3a3a] transition-colors"
          >
            <Wrench className="w-4 h-4" /> Need plumbing service? Request Service
          </button>
        )}
      </div>
    );
  }

  const inputCls =
    'w-full bg-[#0A0A0A] border border-[#262626] text-white rounded-md px-3 py-2.5 placeholder:text-[#525252] focus:outline-none focus:border-[#D4AF37] transition-colors';
  const labelCls = 'block text-xs uppercase tracking-wider text-[#A3A3A3] mb-1.5';

  return (
    <form
      id="promo-intake"
      data-testid="promo-intake-form"
      onSubmit={submit}
      className="w-full max-w-md bg-[#141414] border border-[#262626] rounded-2xl p-6 sm:p-7 shadow-2xl mt-6 space-y-4"
    >
      <div>
        <h2 className="font-['Outfit'] text-2xl font-bold text-white">
          {isGiveaway ? 'Enter Giveaway' : 'Request Service'}
        </h2>
        <p className="text-[#A3A3A3] text-sm mt-1">
          {isGiveaway
            ? 'Enter your details below. No purchase necessary.'
            : "Fill out the form and we'll reach out fast. No account required."}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>First Name *</label>
          <input
            data-testid="intake-first-name"
            className={inputCls}
            value={form.first_name}
            onChange={(e) => set('first_name', e.target.value)}
            autoComplete="given-name"
          />
        </div>
        <div>
          <label className={labelCls}>Last Name *</label>
          <input
            data-testid="intake-last-name"
            className={inputCls}
            value={form.last_name}
            onChange={(e) => set('last_name', e.target.value)}
            autoComplete="family-name"
          />
        </div>
      </div>

      <div>
        <label className={labelCls}>Mobile Phone *</label>
        <input
          data-testid="intake-phone"
          type="tel"
          className={inputCls}
          value={form.phone}
          onChange={(e) => set('phone', e.target.value)}
          placeholder="614-555-1234"
          autoComplete="tel"
        />
      </div>

      <div>
        <label className={labelCls}>Email</label>
        <input
          data-testid="intake-email"
          type="email"
          className={inputCls}
          value={form.email}
          onChange={(e) => set('email', e.target.value)}
          autoComplete="email"
        />
      </div>

      {!isGiveaway && (
      <div>
        <label className={labelCls}>Service Address *</label>
        <input
          data-testid="intake-address"
          className={inputCls}
          value={form.address}
          onChange={(e) => set('address', e.target.value)}
          placeholder="123 Main St"
          autoComplete="street-address"
        />
      </div>
      )}

      {isGiveaway ? (
        <div>
          <label className={labelCls}>ZIP Code (optional)</label>
          <input
            data-testid="intake-zip"
            className={inputCls}
            value={form.zip}
            onChange={(e) => set('zip', e.target.value)}
            autoComplete="postal-code"
            inputMode="numeric"
            placeholder="For eligibility / reporting"
          />
        </div>
      ) : (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>City *</label>
          <input
            data-testid="intake-city"
            className={inputCls}
            value={form.city}
            onChange={(e) => set('city', e.target.value)}
            autoComplete="address-level2"
          />
        </div>
        <div>
          <label className={labelCls}>ZIP *</label>
          <input
            data-testid="intake-zip"
            className={inputCls}
            value={form.zip}
            onChange={(e) => set('zip', e.target.value)}
            autoComplete="postal-code"
            inputMode="numeric"
          />
        </div>
      </div>
      )}

      {!isGiveaway && (
      <div>
        <label className={labelCls}>What do you need?</label>
        <select
          data-testid="intake-need"
          className={inputCls}
          value={form.need}
          onChange={(e) => set('need', e.target.value)}
        >
          <option value="">Choose a category…</option>
          {NEED_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      )}

      {!isGiveaway && (
      <div>
        <label className={labelCls}>How urgent?</label>
        <select
          data-testid="intake-urgency"
          className={inputCls}
          value={form.urgency}
          onChange={(e) => set('urgency', e.target.value)}
        >
          <option value="">Choose…</option>
          {URGENCY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      )}

      {!isGiveaway && (
      <div>
        <label className={labelCls}>Preferred Contact</label>
        <select
          data-testid="intake-preferred-contact"
          className={inputCls}
          value={form.preferred_contact}
          onChange={(e) => set('preferred_contact', e.target.value)}
        >
          <option value="">Choose…</option>
          {CONTACT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      )}

      {!isGiveaway && (
      <div>
        <label className={labelCls}>Notes (optional)</label>
        <textarea
          data-testid="intake-notes"
          rows={3}
          className={inputCls}
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
          placeholder="Tell us anything that helps us prepare."
        />
      </div>
      )}

      <label className="flex items-start gap-3 bg-[#0A0A0A] border border-[#262626] rounded-md p-3 cursor-pointer">
        <input
          data-testid="intake-consent"
          type="checkbox"
          checked={form.consent}
          onChange={(e) => set('consent', e.target.checked)}
          className="mt-1 w-4 h-4 accent-[#D4AF37]"
        />
        <span className="text-[#C9C9C9] text-xs leading-relaxed">
          {isGiveaway ? GIVEAWAY_CONSENT_TEXT : CONSENT_TEXT}
        </span>
      </label>

      {isGiveaway && (
        <label className="flex items-start gap-3 bg-[#0A0A0A] border border-[#262626] rounded-md p-3 cursor-pointer">
          <input
            data-testid="intake-rules-consent"
            type="checkbox"
            checked={!!form.rules_consent}
            onChange={(e) => set('rules_consent', e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#D4AF37]"
          />
          <span className="text-[#C9C9C9] text-xs leading-relaxed">
            I confirm I am entering the NexFlow giveaway and agree to the official rules. No purchase necessary.
            {campaign?.rules_url && (
              <> <a href={campaign.rules_url} target="_blank" rel="noreferrer" className="text-[#D4AF37] underline">Read rules</a>.</>
            )}
          </span>
        </label>
      )}

      {error && (
        <div data-testid="intake-error" className="bg-red-500/10 border border-red-500/40 text-red-300 text-sm rounded-md p-3">
          {error}
        </div>
      )}

      <button
        type="submit"
        data-testid="intake-submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 px-5 py-4 bg-[#D4AF37] text-[#0A0A0A] rounded-lg font-bold text-lg hover:bg-[#F3E5AB] transition-colors disabled:opacity-60"
      >
        {submitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" /> Sending…
          </>
        ) : (
          <>{isGiveaway ? 'Enter Giveaway' : 'Submit Request'}</>
        )}
      </button>
    </form>
  );
};

const PromoPage = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = (slugProp || params.slug || '').toLowerCase();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const intakeRef = useRef(null);

  useEffect(() => {
    if (!slug) {
      setError(true);
      return;
    }
    axios.get(`${API}/public/promo-active/${slug}`)
      .then(({ data: d }) => {
        setData(d);
        if (d?.found && d?.active && d?.destination_type === 'redirect' && d?.redirect_url) {
          window.location.href = d.redirect_url;
        }
      })
      .catch(() => setError(true));
  }, [slug]);

  const scrollToIntake = (e) => {
    e.preventDefault();
    const el = document.getElementById('promo-intake');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
        <div data-testid="promo-error" className="max-w-md text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-amber-300 mb-3" />
          <h1 className="font-['Outfit'] text-2xl font-bold mb-2">Couldn&apos;t load promotion</h1>
          <p className="text-[#A3A3A3] mb-6">Please call us for current offers.</p>
          <a
            href="tel:6142854509"
            data-testid="promo-call-fallback"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#D4AF37] text-[#0A0A0A] rounded-lg font-bold"
          >
            <Phone className="w-5 h-5" /> 614-285-4509
          </a>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div data-testid="promo-loading" className="min-h-screen bg-[#0A0A0A] text-[#A3A3A3] flex items-center justify-center">
        Loading…
      </div>
    );
  }

  if (data?.destination_type === 'redirect' && data?.active && data?.redirect_url) {
    return (
      <div data-testid="promo-redirect" className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
        <p className="text-[#A3A3A3]">Redirecting…</p>
      </div>
    );
  }

  const isInactiveOrMissing = !data.active || !data.found;
  // iter185 — See PromoIntakeForm note. campaign_type may live at top level
  // when the promo_link itself is a giveaway (no scheduled campaign active).
  const isGiveaway =
    data.campaign?.campaign_type === 'giveaway' || data.campaign_type === 'giveaway';
  const callDigits = (data.call_number || '614-285-4509').replace(/[^0-9+]/g, '');
  const callDisplay = fmtPhone(data.call_number || '614-285-4509');

  return (
    <div data-testid="promo-page" className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-start pt-10 pb-16 px-5">
      {/* iter180 — hide the "Made with Emergent" badge on public marketing pages */}
      <style>{`
        #emergent-badge, .emergent-badge, a[href*="emergent.host"], a[href*="emergentagent.com"][target="_blank"][rel*="noopener"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `}</style>
      {/* NexFlow brand strip */}
      <div className="w-full max-w-md text-center mb-8">
        <p data-testid="promo-brand" className="font-['Outfit'] text-[#D4AF37] text-3xl font-bold tracking-tight">NexFlow</p>
        <p className="text-[#A3A3A3] text-xs uppercase tracking-[0.25em] mt-1">Plumbing &amp; Drain Services</p>
      </div>

      {/* Promo card */}
      <div className="w-full max-w-md bg-[#141414] border border-[#262626] rounded-2xl p-7 shadow-2xl">
        <h1
          data-testid="promo-headline"
          className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white leading-tight"
        >
          {data.headline}
        </h1>
        <p
          data-testid="promo-offer-text"
          className="text-[#C9C9C9] text-base mt-3 leading-relaxed whitespace-pre-line"
        >
          {data.offer_text}
        </p>

        {data.promo_code && (
          <div
            data-testid="promo-code-badge"
            className="mt-5 flex items-center justify-between gap-3 bg-[#0A0A0A] border border-[#D4AF37]/40 rounded-lg px-4 py-3"
          >
            <div>
              <p className="text-[#A3A3A3] text-[11px] uppercase tracking-[0.2em]">
                {isGiveaway ? 'Entry Code' : 'Promo Code'}
              </p>
              <p className="text-[#D4AF37] font-['Outfit'] text-2xl font-bold tracking-wider">{data.promo_code}</p>
            </div>
            <p className="text-[#737373] text-[11px] max-w-[140px] text-right leading-snug">
              {isGiveaway ? 'Mention this entry code if you call us.' : 'Mention this code when you call.'}
            </p>
          </div>
        )}

        {isGiveaway && data.campaign?.prize_value && (
          <div data-testid="promo-prize-value" className="mt-4 bg-[#0A0A0A] border border-[#262626] rounded-lg px-4 py-3 text-center">
            <p className="text-[#A3A3A3] text-[11px] uppercase tracking-[0.2em]">Prize Value</p>
            <p className="text-white font-['Outfit'] text-2xl font-bold tracking-wide">{data.campaign.prize_value}</p>
          </div>
        )}

        {isGiveaway && (data.campaign?.fine_print || true) && (
          <p data-testid="promo-fine-print" className="text-[#737373] text-[11px] leading-relaxed mt-3 italic">
            {data.campaign?.fine_print || 'No purchase necessary. One winner selected monthly. Some restrictions apply.'}
          </p>
        )}

        <div className="mt-7 space-y-3">
          <a
            href={`tel:${callDigits}`}
            data-testid="promo-call"
            className="flex items-center justify-center gap-2 w-full px-5 py-4 bg-[#D4AF37] text-[#0A0A0A] rounded-lg font-bold text-lg hover:bg-[#F3E5AB] transition-colors"
          >
            <Phone className="w-5 h-5" /> Call {callDisplay}
          </a>
          {!isInactiveOrMissing && data.intake_form_enabled !== false && (
            <a
              href="#promo-intake"
              onClick={scrollToIntake}
              data-testid="promo-cta"
              className="flex items-center justify-center gap-2 w-full px-5 py-4 bg-[#262626] text-white border border-[#3a3a3a] rounded-lg font-bold hover:bg-[#3a3a3a] transition-colors"
            >
              <Wrench className="w-5 h-5" /> {isGiveaway ? 'Enter Giveaway' : (data.cta_label || 'Request Service')}
            </a>
          )}
        </div>
      </div>

      {/* Intake form */}
      {!isInactiveOrMissing && (data.intake_form_enabled !== false) && (
        <div ref={intakeRef} className="w-full flex justify-center">
          <PromoIntakeForm
            slug={slug}
            callNumber={data.call_number}
            campaign={data.campaign}
            campaignTypeFallback={data.campaign_type}
          />
        </div>
      )}

      <p className="text-[#525252] text-xs mt-8">© NexFlow Plumbing</p>
    </div>
  );
};

export default PromoPage;
